import type { BoardDto, BoardSummaryDto, BoardConfirmsMineDto, BoardConfirmMineItem } from '../../../api/boards';
import { tBoards } from '../../../lib/boards-i18n';
import { computeUnreadKpi } from './board-list-model';
import { buildLinePlot, confirmRing, type ConfirmRing } from './board-kpi-model';

/**
 * 목록 위 숫자 칸 넷 — 시안 `axi-board-list` 의 `.kpis` 를 그대로 따른다 (2026-09-17 사용자 지시).
 *
 * ## 무엇이 바뀌었나
 *
 * 종전에는 넷 중 둘이 「아직 없음」 이었고, 둘째 칸은 시안의 **선 차트**가 아니라 막대였다.
 * 사용자가 「상단의 차트를 시안에 맞춰라」 해서 네 칸을 시안 구조대로 세운다:
 *
 *   ① 안 읽은 글   — 가로 막대 넷(구획별)          `GET /boards` 의 `unreadCount`
 *   ② 주별 새 글   — **선 차트**(면 + 선 + 끝점)   `GET /boards/summary` 의 `weekly.series`
 *   ③ 공지 확인    — **도넛 + 세 줄**              `confirms/mine` 의 첫 글 + 그 글의 확인 수
 *   ④ 확인 요청 공지 — **아바타 목록**             `confirms/mine` 의 `items`
 *
 * ★★★ **데이터가 없어도 «그 카드의 모양»으로 빈다** — 「아직 없음」 이라는 다른 생김새로 갈아타지
 *    않는다. 도넛은 0% 로, 선은 바닥선으로, 목록은 빈 줄로 선다. 그래야 데이터가 들어온 날
 *    화면이 «갑자기 달라지지» 않는다. 0 으로 **숫자를 지어내지는** 않는다(정본 D9-2).
 * ★★ `pendingCount` 와 `items.length` 는 **다르다** — 배지는 `pendingCount` 로 그린다.
 *    목록은 최근 몇 건만 오므로 길이로 세면 틀린다(서버 주석의 경고).
 * ★ 시안의 표본 숫자(20건 · 104/142명)는 옮기지 않았다.
 */

/** 주 시작일(`2026-09-14`)을 칸에 쓸 짧은 꼴(`9/14`)로. */
export function weekLabel(weekStart: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(weekStart);
  if (!m) return weekStart;
  return `${Number(m[1])}/${Number(m[2])}`;
}

/** `2026-09-22T…` → `09-22`. 시안의 목록 줄이 쓰는 꼴. */
function shortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}-${m[3]}` : iso;
}

/** 칸 머리 — 이름 + 배지 + 「표」 펼침(시안 `.vh` · `.tv`). */
function KpiHead({ label, badge, table }: {
  label: string;
  badge?: { text: string; tone?: 'primary' | 'slate' };
  table?: { head: [string, string]; rows: Array<[string, string]> };
}) {
  return (
    <div className="bv1-kpi__head">
      <span className="bv1-kpi__label">{label}</span>
      {badge && (
        <span className={`bv1-kpi__badge${badge.tone === 'slate' ? ' is-slate' : ''}`}>{badge.text}</span>
      )}
      {table && table.rows.length > 0 && (
        <details className="bv1-kpi__tv">
          <summary>{tBoards('boards.v1.kpi.table_toggle')}</summary>
          <table>
            <thead><tr><th>{table.head[0]}</th><th className="is-r">{table.head[1]}</th></tr></thead>
            <tbody>
              {table.rows.map(([k, v]) => (
                <tr key={k}><td>{k}</td><td className="is-r bv1-num">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

/** 큰 숫자 줄 — 값 + 단위 + 곁말(시안 `.vv`). 값이 없으면 대시를 세운다(0 을 지어내지 않는다). */
function KpiValue({ value, unit, note }: { value: string | null; unit?: string; note?: string }) {
  return (
    <div className="bv1-kpi__value">
      <b className="bv1-num">
        {value ?? <span className="bv1-kpi__none">{tBoards('boards.v1.kpi.no_value')}</span>}
        {value !== null && unit && <small>{unit}</small>}
      </b>
      {note && <span className="bv1-kpi__note">{note}</span>}
    </div>
  );
}

/** ① 안 읽은 글 — 구획별 가로 막대 넷. */
function UnreadCard({ boards }: { boards: readonly BoardDto[] }) {
  const kpi = computeUnreadKpi(boards);
  const max = Math.max(1, ...kpi.byGroup.map((g) => g.count));
  const groupLabel = (g: string) => tBoards(`boards.v1.kpi.group.${g}` as never);
  const barsText = kpi.byGroup.map((g) => `${groupLabel(g.group)} ${g.count}`).join(', ');

  return (
    <article className="bv1-card bv1-kpi" data-kpi="unread">
      <KpiHead
        label={tBoards('boards.v1.kpi.unread')}
        badge={{ text: tBoards('boards.v1.kpi.unread_window') }}
        table={{
          head: [tBoards('boards.v1.kpi.th_group'), tBoards('boards.v1.kpi.th_unread')],
          rows: kpi.byGroup.map((g) => [groupLabel(g.group), String(g.count)]),
        }}
      />
      <KpiValue
        value={String(kpi.total)}
        unit={tBoards('boards.v1.unit.posts') || undefined}
        note={kpi.unknown
          ? tBoards('boards.v1.kpi.unread_unknown')
          : tBoards('boards.v1.kpi.unread_note', { n: kpi.boardCount, m: kpi.boardsWithUnread })}
      />
      <div className="bv1-kpi__bars" role="img" aria-label={tBoards('boards.v1.kpi.bars_aria', { list: barsText })}>
        {kpi.byGroup.map((g) => (
          <div key={g.group} className="bv1-kpi__bar">
            <span className="bv1-kpi__bl">{groupLabel(g.group)}</span>
            <span className="bv1-kpi__bt"><i style={{ inlineSize: `${(g.count / max) * 100}%` }} /></span>
            <span className="bv1-kpi__bv bv1-num">{g.count}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

/** ② 주별 새 글 — 선 차트(시안 `.viz-line`). 값이 없으면 바닥선만 남는다. */
function WeeklyCard({ weekly }: { weekly: BoardSummaryDto['weekly'] | null }) {
  const series = weekly?.series ?? [];
  const plot = buildLinePlot(series.map((s) => s.posts));
  const thisWeek = series.length > 0 ? series[series.length - 1]! : null;
  const seriesText = series
    .map((s) => tBoards('boards.v1.kpi.weekly_bar', { w: weekLabel(s.weekStart), n: s.posts }))
    .join(', ');

  return (
    <article className="bv1-card bv1-kpi" data-kpi="weekly">
      <KpiHead
        label={tBoards('boards.v1.kpi.weekly')}
        badge={{ text: tBoards('boards.v1.kpi.weekly_scope'), tone: 'slate' }}
        table={{
          head: [tBoards('boards.v1.kpi.th_week'), tBoards('boards.v1.kpi.th_posts')],
          rows: series.map((s) => [tBoards('boards.v1.kpi.week_of', { w: weekLabel(s.weekStart) }), String(s.posts)]),
        }}
      />
      <KpiValue
        value={thisWeek ? String(thisWeek.posts) : null}
        unit={tBoards('boards.v1.unit.posts') || undefined}
        note={weekly
          ? tBoards('boards.v1.kpi.weekly_note', { c: weekly.comments })
          : tBoards('boards.v1.kpi.not_ready_note')}
      />
      <div className="bv1-kpi__line">
        <div className="bv1-kpi__plot">
          <svg viewBox={`0 0 ${plot.w} ${plot.h}`} role="img"
            aria-label={seriesText || tBoards('boards.v1.kpi.not_ready')}>
            <line className="bv1-kpi__gl" x1={plot.padX} x2={plot.w} y1={plot.midY} y2={plot.midY} />
            <text className="bv1-kpi__tk" x="0" y={plot.midY + 3}>{plot.midLabel}</text>
            {plot.points.length > 1 && <path className="bv1-kpi__area" d={plot.areaD} />}
            {plot.points.length > 1 && <path className="bv1-kpi__ln" d={plot.lineD} />}
            {plot.last && <circle className="bv1-kpi__end" cx={plot.last.x} cy={plot.last.y} r="3.5" />}
          </svg>
        </div>
      </div>
    </article>
  );
}

/** ③ 공지 확인 — 도넛 + 상태 세 줄(시안 `.viz-ring`). */
function ConfirmCard({ ring }: { ring: ConfirmRing | null }) {
  const pct = ring ? ring.percent : 0;
  return (
    <article className="bv1-card bv1-kpi" data-kpi="confirm">
      <KpiHead
        label={tBoards('boards.v1.kpi.confirm')}
        table={ring ? {
          head: [tBoards('boards.v1.kpi.th_state'), tBoards('boards.v1.kpi.th_people')],
          rows: ring.rows.map((r) => [r.label, tBoards('boards.v1.kpi.people', { n: r.count })]),
        } : undefined}
      />
      <KpiValue
        value={ring ? String(ring.confirmed) : null}
        unit={ring ? tBoards('boards.v1.kpi.of_people', { n: ring.total }) : undefined}
        note={ring ? ring.title : tBoards('boards.v1.kpi.confirm_none')}
      />
      <div className="bv1-kpi__ring-row">
        {/* ★ 값이 없으면 «0%» 가 아니라 «—» 다 — 0% 는 「아무도 확인하지 않았다」로 읽힌다. */}
        <div className={`bv1-kpi__ring${ring ? '' : ' is-empty'}`} role="img"
          aria-label={ring
            ? tBoards('boards.v1.kpi.ring_aria', { n: ring.total, m: ring.confirmed })
            : tBoards('boards.v1.kpi.not_ready')}>
          <svg viewBox="0 0 84 84">
            <circle className="bv1-kpi__trk" cx="42" cy="42" r="34" />
            <circle className="bv1-kpi__val" cx="42" cy="42" r="34" pathLength={100}
              strokeDasharray={`${pct} 100`} transform="rotate(-90 42 42)" />
          </svg>
          <span className="bv1-kpi__rc">
            <b className="bv1-num">{ring ? `${pct}%` : tBoards('boards.v1.kpi.no_value')}</b>
            <span>{tBoards('boards.v1.kpi.confirmed_short')}</span>
          </span>
        </div>
        <ul className="bv1-kpi__miss">
          {(ring?.rows ?? []).map((r) => (
            <li key={r.key}>
              <span className="bv1-kpi__mn">{r.label}</span>
              <span className="bv1-kpi__mv bv1-num">{tBoards('boards.v1.kpi.people', { n: r.count })}</span>
              <span className="bv1-kpi__mt"><i style={{ inlineSize: `${r.ratio}%` }} /></span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

/** ④ 확인 요청 공지 — 아바타 목록(시안 `.inboxcard`). */
function InboxCard({ confirms, onOpen }: {
  confirms: BoardConfirmsMineDto | null;
  onOpen: (item: BoardConfirmMineItem) => void;
}) {
  const items = confirms?.items ?? [];
  return (
    <article className="bv1-card bv1-kpi bv1-kpi--inbox" data-kpi="inbox">
      <div className="bv1-kpi__head">
        <span className="bv1-kpi__label">{tBoards('boards.v1.kpi.inbox')}</span>
        {/* ★ 배지는 pendingCount — 목록 길이가 아니다(목록은 최근 몇 건만 온다). */}
        <span className="bv1-kpi__sub bv1-num">
          {tBoards('boards.v1.kpi.inbox_pending', { n: confirms?.pendingCount ?? 0 })}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="bv1-kpi__inbox-empty">{tBoards('boards.v1.kpi.inbox_empty')}</p>
      ) : (
        <ul className="bv1-kpi__inbox">
          {items.map((it) => (
            <li key={it.postId}>
              <button type="button" className="bv1-kpi__lk" onClick={() => onOpen(it)}>
                <span className="wc-sr-only">{tBoards('boards.v1.kpi.open_post', { t: it.title })}</span>
              </button>
              <span className="bv1-kpi__av" aria-hidden>{it.author.name.slice(0, 1)}</span>
              <span className="bv1-kpi__tx">
                <b>{it.title}</b>
                <span>{`${it.author.name} · ${it.board?.name ?? ''} · ${shortDate(it.postedAt)}`}</span>
              </span>
              <span className={`bv1-kpi__badge${it.confirmedAt ? ' is-ok' : ' is-warn'}`}>
                {tBoards(it.confirmedAt ? 'boards.v1.kpi.confirmed' : 'boards.v1.kpi.need_confirm')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export function BoardKpiRow({ boards, summary, confirms, confirmRingSource, onOpenPost }: {
  boards: readonly BoardDto[];
  summary?: BoardSummaryDto | null;
  confirms?: BoardConfirmsMineDto | null;
  /** 도넛에 쓸 글(확인 수가 실린 상세). 없으면 도넛이 0% 로 빈다. */
  confirmRingSource?: Parameters<typeof confirmRing>[0] | null;
  onOpenPost: (item: BoardConfirmMineItem) => void;
}) {
  return (
    <section className="bv1-kpis" aria-label={tBoards('boards.v1.kpi.aria')}>
      <UnreadCard boards={boards} />
      <WeeklyCard weekly={summary?.weekly ?? null} />
      <ConfirmCard ring={confirmRingSource ? confirmRing(confirmRingSource) : null} />
      <InboxCard confirms={confirms ?? null} onOpen={onOpenPost} />
    </section>
  );
}
